import React, { useEffect, useState, useRef } from 'react';
import { databases } from '../../services/appwriteClient';
import { getStudents } from '../../services/studentService';
import { Student, Transaction } from '../../types';
import { FaPrint, FaTrash, FaEnvelope } from 'react-icons/fa';
import { Query } from 'appwrite';
import { sendMail } from '../../services/emailService';
import toast from 'react-hot-toast';
import { generateFeesReminderMail } from '../../utils/MailUtil';
import { fetchTransactionsForStudent } from '../../services/feeTransactionService';

const DATABASE_ID = '682acd26001f69683cd8';
const COLLECTION_ID = '6863f9b6001639720d6e'; // fee transactions

const CabBalances: React.FC = () => {
  const [studentMap, setStudentMap] = useState<{ [id: string]: Student }>({});
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [studentTransactions, setStudentTransactions] = useState<Transaction[]>([]);
  const [studentSearchBalances, setStudentSearchBalances] = useState('');
  const [reminderDueDate, setReminderDueDate] = useState<string | null>(null);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderSending, setReminderSending] = useState(false);
  const studentDetailsRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [availableLevels, setAvailableLevels] = useState<string[]>([]);
  const [availableGroups, setAvailableGroups] = useState<string[]>([]);

  // Fetch all students and map by id for quick lookup
  useEffect(() => {
    setLoading(true);
    getStudents().then(students => {
      const map: { [id: string]: Student } = {};
      const levels = new Set<string>();
      const groups = new Set<string>();
      students.forEach(s => {
        map[s.id] = s;
        if (s.level) levels.add(s.level);
        if (s.group) groups.add(s.group);
      });
      setStudentMap(map);
      setAvailableLevels(Array.from(levels).sort());
      setAvailableGroups(Array.from(groups).sort());
      setLoading(false);
    });
  }, []);

  // Update available groups when level changes
  useEffect(() => {
    if (selectedLevel === 'all') {
      setAvailableGroups(Array.from(new Set(Object.values(studentMap).map(s => s.group).filter(Boolean))).sort());
    } else {
      setAvailableGroups(Array.from(new Set(Object.values(studentMap).filter(s => s.level === selectedLevel).map(s => s.group).filter(Boolean))).sort());
    }
    setSelectedGroup('all');
  }, [selectedLevel, studentMap]);

  // Calculate and sync cab fees
  const syncCabFees = async (student: Student) => {
    try {
      // Fetch all transactions for this student
      const studentTransactions: Transaction[] = await fetchTransactionsForStudent(student.id);
      // Calculate total paid cab fees from transactions
      const cabTransactions = studentTransactions.filter((txn: Transaction) => 
        txn.type === 'cab'
      );
      
      const calculatedPaidFees = cabTransactions.reduce((total: number, txn: Transaction) => {
        return total + parseFloat(txn.amount || '0');
      }, 0);

      // Get current paid_cab_fees from student document
      const currentPaidFees = typeof (student as any).paid_cab_fees === 'number' 
        ? (student as any).paid_cab_fees 
        : parseFloat((student as any).paid_cab_fees || '0');

      // If calculated amount doesn't match stored amount, update the student document
      if (Math.abs(calculatedPaidFees - currentPaidFees) > 0.01) { // Using small threshold for floating point comparison
        await databases.updateDocument(
          DATABASE_ID,
          '682acd33000e34dd93c0', // students collection
          student.id,
          {
            paid_cab_fees: calculatedPaidFees.toString()
          }
        );
        
        // Update the student map with new data
        setStudentMap(prev => ({
          ...prev,
          [student.id]: {
            ...prev[student.id],
            paid_cab_fees: calculatedPaidFees.toString()
          }
        }));
        
        toast.success('Student fees info synced');
      }
    } catch (error) {
      console.error('Error syncing cab fees:', error);
    }
  };

  // Fetch transactions for selected student when dialog opens
  useEffect(() => {
    const fetchStudentTransactions = async () => {
      if (selectedStudent && selectedStudent.id) {
        try {
          // Query fee transactions collection by student_id
          const allTxns: Transaction[] = await fetchTransactionsForStudent(selectedStudent.id);
          setStudentTransactions(allTxns);
          // Sync cab fees after fetching transactions
          await syncCabFees(selectedStudent);
        } catch (err) {
          setStudentTransactions([]);
        }
      } else {
        setStudentTransactions([]);
      }
    };
    fetchStudentTransactions();
  }, [selectedStudent]);

  const handlePrintStudentDetails = () => {
    if (studentDetailsRef.current) {
      const printContents = studentDetailsRef.current.innerHTML;
      const printWindow = window.open('', '', 'height=900,width=1200');
      if (printWindow) {
        printWindow.document.write(`<!DOCTYPE html><html><head><title>Student Cab Details - ${selectedStudent?.name}</title>` +
          `<link href='https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css' rel='stylesheet'>` +
          `</head><body class='bg-white p-8'>` +
          printContents +
          `</body></html>`);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => printWindow.print(), 500);
      }
    }
  };

  return (
    <div className="bg-white rounded shadow p-6">
      <h2 className="text-lg font-semibold mb-4">Cab Balances</h2>
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-end gap-2 md:gap-4">
            <input
              type="text"
              placeholder="Search students..."
              value={studentSearchBalances}
              onChange={e => setStudentSearchBalances(e.target.value)}
              className="border rounded px-3 py-2 w-64"
            />
            <select
              value={selectedLevel}
              onChange={e => setSelectedLevel(e.target.value)}
              className="border rounded px-3 py-2"
            >
              <option value="all">All Levels</option>
              {availableLevels.sort().map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
            <select
              value={selectedGroup}
              onChange={e => setSelectedGroup(e.target.value)}
              className="border rounded px-3 py-2"
            >
              <option value="all">All Groups</option>
              {availableGroups.sort().map(group => (
                <option key={group} value={group}>{group}</option>
              ))}
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Roll No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Cab Fees</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pending</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.values(studentMap)
                  .filter(student =>
                    (selectedLevel === 'all' || student.level === selectedLevel) &&
                    (selectedGroup === 'all' || student.group === selectedGroup) &&
                    (student.name.toLowerCase().includes(studentSearchBalances.toLowerCase()) ||
                     student.register_num.toLowerCase().includes(studentSearchBalances.toLowerCase()))
                  )
                  .map(student => {
                    const total = parseFloat((student as any).total_cab_fees || '0');
                    const paid = typeof (student as any).paid_cab_fees === 'number' ? (student as any).paid_cab_fees : parseFloat((student as any).paid_cab_fees || '0');
                    const pending = total - paid;
                    return (
                      <tr key={student.id} className="cursor-pointer hover:bg-pink-50" onClick={() => setSelectedStudent(student)}>
                        <td className="px-6 py-4 whitespace-nowrap">{student.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{student.register_num}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{total}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{(student as any).paid_cab_fees || 0}</td>
                        <td className="px-6 py-4 whitespace-nowrap font-bold">{pending}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Student Details Dialog */}
      {selectedStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded shadow-md w-full h-full max-w-full max-h-full p-8 overflow-auto relative flex flex-col">
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl"
              onClick={() => setSelectedStudent(null)}
            >
              &times;
            </button>
            
            {/* Actions Section */}
            <div className="absolute top-4 right-12 flex space-x-2">
              <button
                className="bg-pink-600 text-white px-3 py-1 rounded hover:bg-pink-700 flex items-center space-x-1 text-sm"
                onClick={handlePrintStudentDetails}
              >
                <FaPrint className="text-xs" />
                <span>PRINT</span>
              </button>
              <button
                className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 flex items-center space-x-1 text-sm"
                onClick={() => {
                  if (!selectedStudent) return;
                  setReminderDueDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
                  setReminderDialogOpen(true);
                }}
              >
                <FaEnvelope className="text-xs" />
                <span>REMINDER MAIL</span>
              </button>
            </div>
            
            <div ref={studentDetailsRef}>
              <h2 className="text-2xl font-semibold mb-6">Student Cab Details</h2>
              <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><b>Name: </b> {selectedStudent.name}</div>
                <div><b>Roll No: </b> {selectedStudent.register_num}</div>
                <div><b>Total Cab Fees: </b> {(selectedStudent as any).total_cab_fees || 0}</div>
                <div><b>Paid: </b>{(selectedStudent as any).paid_cab_fees || 0}</div>
                <div><b>Pending: </b> {parseFloat((selectedStudent as any).total_cab_fees || '0') - (typeof (selectedStudent as any).paid_cab_fees === 'number' ? (selectedStudent as any).paid_cab_fees : parseFloat((selectedStudent as any).paid_cab_fees || '0'))}</div>
              </div>
              <h3 className="text-lg font-semibold mt-6 mb-2">All Cab Transactions</h3>
              <div className="overflow-x-auto flex-1">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mode</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transaction ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {studentTransactions.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-8">No cab transactions found.</td></tr>
                    ) : (
                      studentTransactions.map(txn =>
                        txn.type == 'cab' ? (
                          <tr key={txn.$id}>
                            <td className="px-6 py-4 whitespace-nowrap">{new Date(txn.time).toLocaleString()}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{txn.mode}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{txn.transaction_id || txn.$id}</td>
                            <td className="px-6 py-4 whitespace-nowrap font-bold">{txn.amount}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{txn.notes || '-'}</td>
                          </tr>
                        ) : null
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reminder Mail Date Dialog */}
      {reminderDialogOpen && selectedStudent && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white p-6 rounded shadow-md max-w-md w-full relative">
            <h2 className="text-lg font-semibold mb-4 text-pink-600">Send Cab Reminder Mail</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Due Date</label>
              <input
                type="date"
                value={reminderDueDate || ''}
                onChange={e => setReminderDueDate(e.target.value)}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                onClick={() => setReminderDialogOpen(false)}
                disabled={reminderSending}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-pink-600 text-white rounded hover:bg-pink-700 disabled:opacity-50"
                onClick={async () => {
                  if (!selectedStudent || !reminderDueDate) return;
                  setReminderSending(true);
                  const email = selectedStudent.email;
                  if (!email) {
                    toast.error('No parent email found!');
                    setReminderSending(false);
                    return;
                  }
                  const total = parseFloat((selectedStudent as any).total_cab_fees || '0');
                  const paid = typeof (selectedStudent as any).paid_cab_fees === 'number' ? (selectedStudent as any).paid_cab_fees : parseFloat((selectedStudent as any).paid_cab_fees || '0');
                  const pending = (total - paid).toString();
                  if ((total-paid) <= 0) {
                    toast.error('No due cab fees')
                    setReminderDialogOpen(false)
                    return
                  }
                  const html = generateFeesReminderMail({
                    studentName: selectedStudent.name,
                    registerId: selectedStudent.register_num,
                    paidAmount: paid.toString(),
                    pendingAmount: pending,
                    dueDate: new Date(reminderDueDate).toLocaleDateString(),
                  });
                  try {
                    await sendMail({
                      to: email,
                      html,
                      subject: 'Cab Fee Payment Reminder',
                    });
                    toast.success('Cab reminder email sent to parent!');
                    setReminderDialogOpen(false);
                  } catch (err: any) {
                    toast.error('Failed to send reminder: ' + (err.message || err));
                  }
                  setReminderSending(false);
                }}
                disabled={reminderSending}
              >
                {reminderSending ? 'Sending...' : 'Send Reminder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CabBalances;
